import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Menu, Database, FileText, Shield, Coins, Sparkles, Heart, Target, Users, Compass, GraduationCap } from 'lucide-react';

const solutions = [
  {
    title: 'Sustainability Data Engineering',
    description: 'Build robust data pipelines, data lakes, and quality rules for ESG data integration.',
    href: '/solutions/data-engineering',
    icon: Database,
  },
  {
    title: 'Sustainability Reporting & Assurance',
    description: 'GHG inventory automation, ISSB/IFRS, GRI, TCFD compliance, and audit-ready packs.',
    href: '/solutions/reporting',
    icon: FileText,
  },
  {
    title: 'dMRV Turnkey Solution',
    description: 'Complete MRV framework with evidence capture, verification-ready data rooms.',
    href: '/solutions/dmrv',
    icon: Shield,
  },
  {
    title: 'Tokenization & Blockchain',
    description: 'Tokenized credits, issuance registry, proof-of-origin, and retirement tracking.',
    href: '/solutions/tokenization',
    icon: Coins,
  },
  {
    title: 'Credibility Engine',
    description: 'Credibility scoring, fraud-risk detection, and finance-grade reporting.',
    href: '/solutions/credibility-engine',
    icon: Sparkles,
  },
  {
    title: 'SocTech (Social Impact Technology)',
    description: 'Technology engineered for measurable social and environmental impact.',
    href: '/solutions/soctech',
    icon: Heart,
  },
];

const services = [
  {
    title: 'Materiality Assessment',
    description: 'Structured, defensible materiality assessments aligned with global standards.',
    href: '/services/materiality-assessment',
    icon: Target,
  },
  {
    title: 'ESG Reporting & Compliance',
    description: 'Compliance-ready, investor-aligned, industry-specific ESG reports.',
    href: '/services/esg-reporting',
    icon: FileText,
  },
  {
    title: 'ESG Services Outsourcing',
    description: 'Your embedded ESG function without expanding headcount.',
    href: '/services/esg-outsourcing',
    icon: Users,
  },
  {
    title: 'Strategy & Program Design',
    description: 'Turn ESG ambition into operational execution.',
    href: '/services/strategy-program-design',
    icon: Compass,
  },
  {
    title: 'Data Curation & Management',
    description: 'Build finance-grade ESG data infrastructure.',
    href: '/services/data-curation',
    icon: Database,
  },
  {
    title: 'Training & Enablement',
    description: 'Build capability, activate strategy, scale impact.',
    href: '/services/training-enablement',
    icon: GraduationCap,
  },
];

const industries = [
  { title: 'IT-BPM & Business Process Outsourcing', href: '/industries/itbpm' },
  { title: 'Real Estate & Built Environment', href: '/industries/real-estate' },
  { title: 'Manufacturing', href: '/industries/manufacturing' },
  { title: 'Logistics & Supply Chain', href: '/industries/logistics' },
  { title: 'Hospitality & Tourism', href: '/industries/hospitality' },
  { title: 'Public Sector / Smart Cities', href: '/industries/public-sector' },
];

function ListItem({ className, title, children, icon: Icon, to }) {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          to={to}
          className={cn(
            'block select-none space-y-1 rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-muted focus:bg-muted',
            className
          )}
        >
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-teal" />}
            <div className="text-sm font-medium leading-none text-foreground">{title}</div>
          </div>
          {children && (
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1.5">
              {children}
            </p>
          )}
        </Link>
      </NavigationMenuLink>
    </li>
  );
}

function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(function() {
    function handleScroll() {
      setIsScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll);
    return function() {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(function() {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-sm border-b border-border'
          : 'bg-transparent'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-18 lg:h-20">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_1f762a5e-4e34-4452-aabe-298bcfb2d13e/artifacts/9emip28a_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg"
              alt="SusTek"
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-muted">
                    Solutions
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[600px] gap-3 p-4 md:grid-cols-2">
                      {solutions.map(function(solution) {
                        return (
                          <ListItem
                            key={solution.title}
                            title={solution.title}
                            to={solution.href}
                            icon={solution.icon}
                          >
                            {solution.description}
                          </ListItem>
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-muted">
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[600px] gap-3 p-4 md:grid-cols-2">
                      {services.map(function(service) {
                        return (
                          <ListItem
                            key={service.title}
                            title={service.title}
                            to={service.href}
                            icon={service.icon}
                          >
                            {service.description}
                          </ListItem>
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-muted">
                    Industries
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-1 p-3">
                      {industries.map(function(industry) {
                        return (
                          <ListItem key={industry.title} title={industry.title} to={industry.href} />
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/case-studies"
                    className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus:bg-muted focus:outline-none"
                  >
                    Case Studies
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/insights"
                    className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus:bg-muted focus:outline-none"
                  >
                    Insights
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link
                    to="/about"
                    className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-muted focus:bg-muted focus:outline-none"
                  >
                    About
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/contact">
              <Button variant="ghost" size="sm">
                Contact
              </Button>
            </Link>
            <Link to="/book-call">
              <Button variant="nav-cta" size="default">
                Book a Discovery Call
              </Button>
            </Link>
          </div>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-[400px] p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <Link to="/" onClick={function() { setMobileOpen(false); }}>
                    <img
                      src="https://customer-assets.emergentagent.com/job_1f762a5e-4e34-4452-aabe-298bcfb2d13e/artifacts/9emip28a_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg"
                      alt="SusTek"
                      className="h-8 w-auto"
                    />
                  </Link>
                </div>

                <div className="flex-1 overflow-y-auto py-4 px-4">
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Solutions
                    </h3>
                    <ul className="space-y-1">
                      {solutions.map(function(solution) {
                        var IconComp = solution.icon;
                        return (
                          <li key={solution.title}>
                            <Link
                              to={solution.href}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                              onClick={function() { setMobileOpen(false); }}
                            >
                              <IconComp className="h-5 w-5 text-teal" />
                              <span className="text-sm font-medium">{solution.title}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Services
                    </h3>
                    <ul className="space-y-1">
                      {services.map(function(service) {
                        var IconComp = service.icon;
                        return (
                          <li key={service.title}>
                            <Link
                              to={service.href}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                              onClick={function() { setMobileOpen(false); }}
                            >
                              <IconComp className="h-5 w-5 text-teal" />
                              <span className="text-sm font-medium">{service.title}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Industries
                    </h3>
                    <ul className="space-y-1">
                      {industries.map(function(industry) {
                        return (
                          <li key={industry.title}>
                            <Link
                              to={industry.href}
                              className="block p-3 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
                              onClick={function() { setMobileOpen(false); }}
                            >
                              {industry.title}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="space-y-1">
                    <Link
                      to="/case-studies"
                      className="block p-3 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
                      onClick={function() { setMobileOpen(false); }}
                    >
                      Case Studies
                    </Link>
                    <Link
                      to="/insights"
                      className="block p-3 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
                      onClick={function() { setMobileOpen(false); }}
                    >
                      Insights
                    </Link>
                    <Link
                      to="/about"
                      className="block p-3 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
                      onClick={function() { setMobileOpen(false); }}
                    >
                      About
                    </Link>
                    <Link
                      to="/contact"
                      className="block p-3 rounded-lg hover:bg-muted transition-colors text-sm font-medium"
                      onClick={function() { setMobileOpen(false); }}
                    >
                      Contact
                    </Link>
                  </div>
                </div>

                <div className="p-4 border-t border-border">
                  <Link to="/book-call" onClick={function() { setMobileOpen(false); }}>
                    <Button variant="teal" className="w-full" size="lg">
                      Book a Discovery Call
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
}

export default Navigation;
